import PatternBackground from "@/components/PatternBackground";
import OverlayCard from "@/components/OverlayCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const bookingSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50, "Name too long"),
  lastName: z.string().trim().min(1, "Last name is required").max(50, "Name too long"),
  email: z.string().trim().email("Invalid email address").max(255, "Email too long"),
  roomType: z.string().min(1, "Please select a room type"),
  checkin: z.string().min(1, "Check-in date is required"),
  checkout: z.string().min(1, "Check-out date is required"),
  guests: z.string().min(1, "Number of guests is required").refine(v => {
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 && n <= 100;
  }, "Number of guests must be between 1 and 100"),
  message: z.string().max(1000, "Message too long").optional()
});

const BookYourStay = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roomType: "",
    checkin: "",
    checkout: "",
    guests: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Validate form data
      const validatedData = bookingSchema.parse(formData);
      
      const response = await fetch(`https://aqehupafavmjsmzdnotd.supabase.co/functions/v1/send-booking-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validatedData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send booking request');
      }

      toast.success("Your booking request has been sent! We'll get back to you shortly.");
      
      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        roomType: "",
        checkin: "",
        checkout: "",
        guests: "",
        message: ""
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.issues[0];
        toast.error(firstError.message);
      } else {
        console.error('Error sending booking request:', error);
        toast.error("Sorry, there was an error sending your request. Please try again or contact us at reservations@kijaniarushaairportlodge.com");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PatternBackground pattern="woven">
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Page Title */}
          <div className="text-center mb-16">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Book Your Stay
            </h1>
          </div>

          {/* Live Availability Section */}
          <div className="max-w-4xl mx-auto">
            <OverlayCard size="lg" className="mb-12">
              <h2 className="font-heading text-3xl font-bold text-foreground mb-6">
                Live Availability
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                See which rooms types are available below. Then <a href="mailto:reservations@kijaniarushaairportlodge.com" className="text-primary underline hover:text-primary/80">contact us</a> to make a booking. A member of the team will get back 
                to you shortly.
              </p>

              <div className="space-y-6 mb-8">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">LOW SEASON</h3>
                  <p className="text-sm text-muted-foreground">1st November to 15th December</p>
                  <p className="text-sm text-muted-foreground">1st March to 31st May</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">HIGH SEASON</h3>
                  <p className="text-sm text-muted-foreground">16th December to 28th February</p>
                  <p className="text-sm text-muted-foreground">1st June to 31st October</p>
                </div>
              </div>

              <div className="space-y-6 mb-8">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">RESIDENTS</h3>
                  <p className="text-sm text-muted-foreground">
                    If you are an East African citizen or resident, you are entitled to discounted rates at Kijani Arusha Airport Lodge. Please <a href="mailto:reservations@kijaniarushaairportlodge.com" className="text-primary underline hover:text-primary/80">contact
                    us</a> for more information and rates, or fill out the form below.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">FULL EXCLUSIVITY</h3>
                  <p className="text-sm text-muted-foreground">
                    Kijani Arusha Airport Lodge can be booked on an exclusive basis for groups or large families. Discounts for the full property are
                    applicable. Please <a href="mailto:reservations@kijaniarushaairportlodge.com" className="text-primary underline hover:text-primary/80">contact us</a> for more information and rates, or fill out the form below.
                  </p>
                </div>
              </div>
            </OverlayCard>

            {/* Contact Form */}
            <OverlayCard size="lg">
              <h3 className="font-heading text-2xl font-bold text-foreground mb-6">QUERIES</h3>
              <p className="text-muted-foreground mb-6">
                For any queries or additional information, please fill out the form below.
                A member of the team will get back to you shortly.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input 
                      id="firstName" 
                      type="text" 
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({...prev, firstName: e.target.value}))}
                      required 
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input 
                      id="lastName" 
                      type="text" 
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({...prev, lastName: e.target.value}))}
                      required 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                      required 
                    />
                  </div>
                  <div>
                    <Label htmlFor="roomType">Preferred Room Type</Label>
                    <select 
                      id="roomType" 
                      value={formData.roomType}
                      onChange={(e) => setFormData(prev => ({...prev, roomType: e.target.value}))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select Room Type</option>
                      <option value="single">Single Room</option>
                      <option value="double">Double/Twin Room</option>
                      <option value="family">Family Room</option>
                      <option value="multiple">Multiple Rooms</option>
                      <option value="exclusive">Entire Lodge (Exclusive Booking)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="checkin">Check-in Date</Label>
                    <Input 
                      id="checkin" 
                      type="date" 
                      value={formData.checkin}
                      onChange={(e) => setFormData(prev => ({...prev, checkin: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkout">Check-out Date</Label>
                    <Input 
                      id="checkout" 
                      type="date" 
                      value={formData.checkout}
                      onChange={(e) => setFormData(prev => ({...prev, checkout: e.target.value}))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="guests">Number of Guests</Label>
                  <Input 
                    id="guests" 
                    type="number" 
                    min="1" 
                    max="100" 
                    placeholder="1" 
                    value={formData.guests}
                    onChange={(e) => setFormData(prev => ({...prev, guests: e.target.value}))}
                  />
                </div>

                <div>
                  <Label htmlFor="message">Special Requests or Message</Label>
                  <Textarea 
                    id="message" 
                    rows={6} 
                    placeholder="Any special requirements, dietary restrictions, or other requests..." 
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({...prev, message: e.target.value}))}
                  />
                </div>

                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={isSubmitting}>
                  {isSubmitting ? "SUBMITTING..." : "SUBMIT BOOKING REQUEST"}
                </Button>
              </form>
            </OverlayCard>
          </div>

        </div>
      </div>
    </PatternBackground>
  );
};

export default BookYourStay;